import { useState, useEffect, useCallback, useRef } from 'react';
import type { ChatSession, DisplayMessage } from '../types';

function getStorageKey(tenant: string, key: string): string {
  return `qbrains:${tenant}:${key}`;
}

interface SessionContext {
  locale?: string;
  url?: string;
  page_title?: string;
  user_id?: string;
  user_name?: string;
  user_email?: string;
  user_phone?: string;
}

export function useSession(tenant: string | null, context?: SessionContext, ready = true) {
  const [session, setSession] = useState<ChatSession | null>(null);
  const [history, setHistory] = useState<DisplayMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initRef = useRef(false);

  const initSession = useCallback(async () => {
    if (!tenant || !ready || initRef.current) return;
    initRef.current = true;

    try {
      // Check for existing session token and visitor_id
      const storedToken = localStorage.getItem(getStorageKey(tenant, 'session_token'));
      const storedVisitorId = localStorage.getItem(getStorageKey(tenant, 'visitor_id'));

      const body: Record<string, unknown> = {
        tenant_slug: tenant,
        context: context || {},
      };

      // Send visitor_id only if we have one from a previous session;
      // otherwise the server generates a cryptographically secure one.
      if (storedVisitorId) {
        body.visitor_id = storedVisitorId;
      }

      if (storedToken) {
        body.session_token = storedToken;
      }

      const apiBase = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${apiBase}/api/v1/chat/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        throw new Error(`Session init failed: ${res.status}`);
      }

      const data = await res.json();
      // API returns flat: { session_token, conversation_id, visitor_id, history }
      const chatSession: ChatSession = data.session ?? {
        session_token: data.session_token,
        tenant_id: '',
        conversation_id: data.conversation_id,
        visitor_id: data.visitor_id || storedVisitorId || '',
        contact_id: '',
        context: context || {},
        created_at: new Date().toISOString(),
        last_active_at: new Date().toISOString(),
      };
      const messages: DisplayMessage[] = (data.history || []).map(
        (m: { id?: string; role: string; content: string; timestamp?: string; createdAt?: string; parts?: DisplayMessage['parts']; file?: DisplayMessage['file'] }) => ({
          id: m.id || crypto.randomUUID(),
          role: m.role === 'agent' || m.role === 'assistant' ? 'agent' : m.role === 'system' ? 'system' : 'customer',
          content: m.content,
          timestamp: m.timestamp || m.createdAt || new Date().toISOString(),
          parts: m.parts,
          file: m.file,
        })
      );

      localStorage.setItem(getStorageKey(tenant, 'session_token'), chatSession.session_token);
      if (chatSession.visitor_id) {
        localStorage.setItem(getStorageKey(tenant, 'visitor_id'), chatSession.visitor_id);
      }

      setSession(chatSession);
      setHistory(messages);
      setError(null);
      setLoading(false);
    } catch (err) {
      // Allow retry on failure
      initRef.current = false;
      setError(err instanceof Error ? err.message : 'Session init failed');
      setLoading(false);
    }
  }, [tenant, context, ready]);

  useEffect(() => {
    initSession();
  }, [initSession]);

  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    initRef.current = false;
    initSession();
  }, [initSession]);

  return { session, history, loading, error, retry };
}
